import moment from 'moment'
import { sharedDataStore } from 'src/store/DataStore'
import { User } from 'src/models/User'
import { sharedAppStateStore } from 'src/store/AppStateStore'
import { FjEvent } from 'src/models/FjEvent'
import posthog from 'posthog-js'

export class Analytics {
  static shouldTrack() {
    return process.env.REACT_APP_POSTHOG_KEY && process.env.NODE_ENV !== 'development'
  }

  static loadAnalytics() {
    if (Analytics.shouldTrack()) {
      posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
        api_host: 'https://ph.flockjay.com',
        person_profiles: 'identified_only',
        ui_host: 'https://us.posthog.com',
      })
    }
  }

  static trackFlockjayEvent = async (eventType: string, user?: string, data?: object) => {
    const event = new FjEvent()
    await event.save({
      eventType,
      user,
      data: { ...data, sessionId: sharedAppStateStore.sessionId },
    })
  }

  static trackPage() {
    if (Analytics.shouldTrack()) {
      posthog.capture('$pageview')
    }
  }

  static trackEvent(eventType: string, properties: object) {
    if (Analytics.shouldTrack()) {
      try {
        posthog.capture(eventType, properties)
      } catch (err) {
        // sharedAppStateStore.handleError(err, undefined, false)
      }
    }
  }

  static trackIdentify(user?: User) {
    if (!user || user.isAnonymous() || !Analytics.shouldTrack()) return

    const traits = {
      name: user.fullName,
      // email: user.email,
      accessRole: user.accessRole,
      // groupIds: user.groupIds,
      // groupNames: user.groups.map((group) => group.name),
    }

    // if (user.manager) {
    //   traits['managerId'] = user.manager.id
    //   traits['managerName'] = user.manager.fullName
    // }

    if (user.profileImgUrl) traits['avatar'] = user.profileImgUrl

    posthog.identify(user.id, traits)
    posthog.group('company', user.company.id, { name: user.company.name })
  }

  static login = async (user: string, isRefresh: boolean) => {
    await Analytics.trackFlockjayEvent('login', user, { is_refresh: isRefresh })
    Analytics.trackEvent('login', { isRefresh })
  }

  static logout = () => posthog.reset()

  static search = async (user: string, data: object) => {
    await Analytics.trackFlockjayEvent('search', user, { ...data, searchStr: data['searchStr']?.trim() })
  }

  static saveLocalTrackingData = () => {
    Analytics.saveAllViewEvents()
  }

  static saveAllViewEvents = () => {
    Object.keys(sharedDataStore.viewTrackingData).forEach((trackingId) => {
      Analytics.saveViewEvent(trackingId)
    })
  }

  static saveViewEvent = async (trackingId: string) => {
    try {
      const { eventType, user, ...data } = sharedDataStore.viewTrackingData[trackingId]
      await Analytics.trackFlockjayEvent(eventType, user, data)
      delete sharedDataStore.viewTrackingData[trackingId]
    } catch (err) {
      sharedAppStateStore.handleError(err, { alert: false })
    }
  }

  static trackAssetDownload = async (user: string, assetId: string, context: object = {}) => {
    await Analytics.trackFlockjayEvent('asset_download', user, { assetId, ...context })
  }

  static trackAIFeedback = async (
    eventType: 'ai_summary_feedback' | 'ai_grading_feedback',
    user: string,
    rating: string,
    feedback?: string,
    context: object = {}
  ) => {
    try {
      const eventData = { rating, ...context }
      if (feedback) eventData['feedback'] = feedback
      await Analytics.trackFlockjayEvent(eventType, user, eventData)
    } catch (err) {
      sharedAppStateStore.handleError(err, { alert: false })
    }
  }
}

export class MediaTracker {
  mediaElement: HTMLVideoElement | HTMLAudioElement
  context: object
  event: FjEvent
  data: any = {}
  intervalId?: number
  mediaFullyConsumed?: () => void
  playbackTimeCache: number = 0

  constructor(mediaElement: HTMLVideoElement | HTMLAudioElement, context: object) {
    this.mediaElement = mediaElement
    this.context = context
    this.data = {}

    // We only need to use these 3 handlers
    // If user scrubs/seeks while playing the flow is:
    // stop => seeks => seeked => timeupdate => play
    // Because we cache the last play time in timeupdate we can accurately
    // know where the user jumped from and to
    this.mediaElement.onplay = this.playHandler
    this.mediaElement.onpause = this.stopHandler
    this.mediaElement.ontimeupdate = this.onTimeUpdateHandler
  }

  saveWatchEvent = async (data: any) => {
    this.data = data
    const eventType = this.mediaElement instanceof HTMLVideoElement ? 'video_watched' : 'audio_listened'
    try {
      const newEvent = this.event ? FjEvent.fromData({ ...this.event }) : new FjEvent()
      await newEvent.save({
        eventType,
        user: sharedDataStore.user.id,
        data: { ...data, sessionId: sharedAppStateStore.sessionId },
      })
      this.event = newEvent
    } catch (err) {
      sharedAppStateStore.handleError(err, { alert: false })
    }
  }

  playHandler = () => {
    const { timings = [] } = this.data
    timings.push({
      playback_start: this.mediaElement.currentTime,
      start_timestamp: moment().toISOString(),
    })

    this.saveWatchEvent({ ...this.context, timings })

    // There are some cases where we have multiple timers running
    // so we kill any active timers before starting a new one
    this.clearInterval()

    this.intervalId = window.setInterval(() => {
      this.updateTiming(undefined)
    }, 15000)
  }

  stopHandler = () => {
    const endTime =
      Math.abs(this.mediaElement.currentTime - this.playbackTimeCache) > 15 ? this.playbackTimeCache : undefined
    this.updateTiming(endTime)
    this.clearInterval()
  }

  endTracking = () => {
    this.stopHandler()
    this.mediaElement.onplay = null
    this.mediaElement.onpause = null
    this.mediaElement.ontimeupdate = null
    this.mediaElement.onseeking = null
    this.mediaElement.onseeked = null
    if (!this.mediaElement.paused) this.mediaElement.pause()
    this.data = {}
  }

  onTimeUpdateHandler = () => {
    if (this.mediaElement.paused) return
    this.playbackTimeCache = this.mediaElement.currentTime
  }

  clearInterval = () => {
    if (this.intervalId) {
      window.clearInterval(this.intervalId)
      this.intervalId = undefined
    }
  }

  updateTiming = async (endTime?: number) => {
    const { timings = [] } = this.data
    if (timings.length > 0) {
      timings[timings.length - 1]['playback_stop'] = endTime || this.mediaElement.currentTime
      timings[timings.length - 1][`end_timestamp`] = moment().toISOString()
      this.saveWatchEvent({ ...this.data, timings })
    }
  }
}
